import { useCallback, useEffect, useState } from "react";

export type ReaderTheme = "light" | "eye" | "dark";

export interface ReaderSettings {
  fontSize: number;
  theme: ReaderTheme;
}

const STORAGE_KEY = "yushuwu:reader-settings";
const DEFAULTS: ReaderSettings = { fontSize: 18, theme: "light" };

function readSettings(): ReaderSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<ReaderSettings>;
    return {
      fontSize:
        typeof parsed.fontSize === "number" && parsed.fontSize >= 14 && parsed.fontSize <= 30
          ? parsed.fontSize
          : DEFAULTS.fontSize,
      theme:
        parsed.theme === "eye" || parsed.theme === "dark" || parsed.theme === "light"
          ? parsed.theme
          : DEFAULTS.theme,
    };
  } catch {
    return DEFAULTS;
  }
}

export function useReaderSettings() {
  const [settings, setSettings] = useState<ReaderSettings>(DEFAULTS);

  useEffect(() => {
    setSettings(readSettings());
  }, []);

  const persist = useCallback((next: ReaderSettings) => {
    setSettings(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const setFontSize = useCallback(
    (fontSize: number) => {
      persist({ ...readSettings(), fontSize });
    },
    [persist],
  );

  const setTheme = useCallback(
    (theme: ReaderTheme) => {
      persist({ ...readSettings(), theme });
    },
    [persist],
  );

  return { settings, setFontSize, setTheme };
}